/**
 * Shared TypeScript types for Scholarship Management System
 * Used across client and server
 */

// ENUMS
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  // Read-only staff role — sees everything an Admin sees, can perform no
  // mutating action.
  VIEWER = 'viewer',
  APPLICANT = 'applicant',
  GUEST = 'guest'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  DEACTIVATED = 'deactivated'
}

export enum ApplicationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  DOCUMENT_VERIFICATION = 'document_verification',
  INTERVIEW = 'interview',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  NEEDS_REVISION = 'needs_revision'
}

export enum DocumentVerificationStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
  REQUESTING_REVISION = 'requesting_revision'
}

export enum ScholarStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  GRADUATED = 'graduated',
  TERMINATED = 'terminated'
}

export enum RenewalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  UNDER_REVIEW = 'under_review'
}

export enum AllowanceStatus {
  PENDING = 'pending',
  RELEASED = 'released',
  CANCELLED = 'cancelled'
}

export enum ViolationSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum AnnouncementType {
  NEWS = 'news',
  DEADLINE = 'deadline',
  EVENT = 'event',
  SCHOLARSHIP_UPDATE = 'scholarship_update',
  MAINTENANCE = 'maintenance'
}

export enum NotificationType {
  APPLICATION_SUBMITTED = 'application_submitted',
  DOCUMENTS_MISSING = 'documents_missing',
  APPLICATION_APPROVED = 'application_approved',
  APPLICATION_REJECTED = 'application_rejected',
  RENEWAL_DUE = 'renewal_due',
  ANNOUNCEMENT_POSTED = 'announcement_posted',
  SCHOLARSHIP_POSTED = 'scholarship_posted',
  SYSTEM_NOTIFICATION = 'system_notification'
}

// USER

// Mirrors server/src/lib/adminPermissions.ts. Limits are null for "no
// limit" and 0 for "switched off".
export interface AdminPermissions {
  resetPassword: boolean;
  disableAccount: boolean;
  deleteAccount: boolean;
  assignRoles: boolean;
  registerUser: boolean;
  renewalRequests: boolean;
  applicationReports: boolean;
  editSignatories: boolean;
  themes: boolean;
  maxPrograms: number | null;
  announcementsPerDay: number | null;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  emailVerified: boolean;
  profilePictureUrl?: string;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
  isDeletionReviewer: boolean;
  // Admin accounts only — what the Super Admin has left switched on.
  // Always complete when present. See utils/adminPermissions.ts.
  adminPermissions?: AdminPermissions;
  // Someone else chose this password (a staff reset or a staff-created
  // account) — the app shows nothing but the change-password screen
  // until it's replaced.
  mustChangePassword: boolean;
  // When the holder last chose their own password: the start of the
  // once-a-week window. Undefined if they never have.
  passwordChangedAt?: string;
  // When this account agreed to the Data Privacy Policy, and to which
  // version of its text. Undefined for accounts created before consent
  // was collected -- they were never asked.
  privacyConsentAt?: Date;
  privacyConsentVersion?: string;
  // The same for the Terms and Conditions. One tick box covers both, so
  // these normally match the pair above; they are separate because the
  // two documents are versioned separately.
  termsConsentAt?: Date;
  termsConsentVersion?: string;
  // Only ever set for an Applicant-role account (joined from its
  // Applicant profile) — backs the Senior High School/ALS/College/
  // Special Course filter on User Management.
  yearLevel?: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  profilePictureUrl?: string;
  status?: UserStatus;
}

// AUTHENTICATION
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresIn: number;
}

// The "take a ticket" login queue — see server/src/middleware/auth.ts's
// joinLoginQueue/checkLoginQueue. `position` is 0 once `ready` is true
// (this ticket has earned a reserved login slot); otherwise it's this
// ticket's place in line. `number` is permanent (assigned once, at join
// time), unlike position which shifts as others ahead finish or abandon.
export interface QueueTicketStatus {
  ticket: string;
  number: number;
  position: number;
  ready: boolean;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  newPassword: string;
}

export interface JWTPayload {
  sub: number;
  email: string;
  role: UserRole;
  iat: number;
  exp: number;
}

// APPLICANT
export interface FamilyMemberDetail {
  name?: string;
  occupation?: string;
  monthlyIncome?: number;
  educationalAttainment?: string;
  // Only meaningful for guardian — father/mother's relationship to the
  // applicant is fixed and never collected.
  relationship?: string;
}

export interface Applicant {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  suffix?: string;
  dateOfBirth?: Date;
  age?: number;
  sex?: string;
  civilStatus?: string;
  contactNumber?: string;
  // Where application status updates and new-program announcements are
  // sent — falls back to the account login email when not set.
  contactEmail?: string;
  // III. Contact Details — Messenger handle or profile link, asked
  // alongside the mobile number since it is the channel most applicants
  // here actually check.
  messengerAccount?: string;
  // Mirrored from the User record so profile completeness can require
  // it — the photo is uploaded separately from this form, but the office
  // treats it as part of a complete profile.
  profilePictureUrl?: string;
  address?: string;
  schoolName?: string;
  courseName?: string;
  yearLevel?: string;
  municipality?: string;
  barangay?: string;
  zipCode?: string;
  householdMonthlyIncome?: number;

  // I. Personal Information
  placeOfBirth?: string;
  nationality?: string;
  idType?: string;
  idNumber?: string;

  // II. IP Affiliation
  isIndigenousPeople?: boolean;
  ipGroupTribe?: string;

  // III. Contact Details
  province?: string;

  // IV. Socio-economic classification
  sectoralClassifications: string[];
  sectoralClassificationOther?: string;

  // V. Family background
  numberOfHouseholdMembers?: number;
  numberOfDependentsStudying?: number;
  parentalStatus?: string;
  father?: FamilyMemberDetail;
  mother?: FamilyMemberDetail;
  guardian?: FamilyMemberDetail;

  // VI. Educational background
  schoolAddress?: string;
  schoolType?: string;
  gwa?: number;
  previousSchool?: string;
  honorsAwards?: string;
  academicStatus?: string;

  // VII. Other educational assistance
  currentlyReceivingAssistance?: boolean;
  currentAssistanceProgram?: string;
  currentAssistanceAmount?: number;
  appliedOtherScholarship?: boolean;
  otherScholarshipProgram?: string;
  receivedCeapAssistance?: boolean;
  ceapAssistanceAmount?: number;
  academicDistinctionExtracurricular?: string;

  // IX. ATM
  lbpAtmAccountNumber?: string;

  // True once the student has submitted any application — Edit
  // Information and Request Renewal are then disabled until an Admin/
  // Super Admin re-enables it.
  profileLocked: boolean;

  createdAt: Date;
  updatedAt: Date;
}

export interface CreateApplicantRequest {
  middleName?: string;
  suffix?: string;
  dateOfBirth: string;
  sex: string;
  civilStatus: string;
  contactNumber: string;
  address: string;
  municipality: string;
  barangay: string;
  zipCode: string;
  householdMonthlyIncome?: number;
}

// Full profile update — every field optional so students can save the
// form incrementally across sessions. Required-ness is enforced only at
// application-submit time.
export interface UpdateApplicantProfileRequest {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  suffix?: string;
  dateOfBirth?: string;
  age?: number;
  sex?: string;
  civilStatus?: string;
  contactNumber?: string;
  // Where application status updates and new-program announcements are
  // sent — falls back to the account login email when not set.
  contactEmail?: string;
  // III. Contact Details — Messenger handle or profile link, asked
  // alongside the mobile number since it is the channel most applicants
  // here actually check.
  messengerAccount?: string;
  address?: string;
  schoolName?: string;
  courseName?: string;
  yearLevel?: string;
  municipality?: string;
  barangay?: string;
  zipCode?: string;
  householdMonthlyIncome?: number;
  placeOfBirth?: string;
  nationality?: string;
  idType?: string;
  idNumber?: string;
  isIndigenousPeople?: boolean;
  ipGroupTribe?: string;
  province?: string;
  sectoralClassifications?: string[];
  sectoralClassificationOther?: string;
  numberOfHouseholdMembers?: number;
  numberOfDependentsStudying?: number;
  parentalStatus?: string;
  schoolAddress?: string;
  schoolType?: string;
  gwa?: number;
  previousSchool?: string;
  honorsAwards?: string;
  academicStatus?: string;
  currentlyReceivingAssistance?: boolean;
  currentAssistanceProgram?: string;
  currentAssistanceAmount?: number;
  appliedOtherScholarship?: boolean;
  otherScholarshipProgram?: string;
  receivedCeapAssistance?: boolean;
  ceapAssistanceAmount?: number;
  academicDistinctionExtracurricular?: string;
  lbpAtmAccountNumber?: string;
  father?: FamilyMemberDetail;
  mother?: FamilyMemberDetail;
  guardian?: FamilyMemberDetail;
}

export interface ProfileCompleteness {
  complete: boolean;
  missingFields: string[];
  missingDocuments: string[];
}

// EDUCATIONAL RECORD
export interface EducationalRecord {
  id: number;
  applicantId: number;
  schoolId?: number;
  studentId: string;
  courseId?: number;
  yearLevel: string;
  semester: string;
  gpa: number;
  enrollmentStatus: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateEducationalRecordRequest {
  schoolId?: number;
  studentId: string;
  courseId?: number;
  yearLevel: string;
  semester: string;
  gpa: number;
  enrollmentStatus: string;
}

// FAMILY MEMBER
export interface FamilyMember {
  id: number;
  applicantId: number;
  name: string;
  relationship: string;
  occupation?: string;
  contactNumber?: string;
  memberType: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateFamilyMemberRequest {
  name: string;
  relationship: string;
  occupation?: string;
  contactNumber?: string;
  memberType: string;
}

// EMERGENCY CONTACT
export interface EmergencyContact {
  id: number;
  applicantId: number;
  name: string;
  relationship: string;
  contactNumber: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateEmergencyContactRequest {
  name: string;
  relationship: string;
  contactNumber: string;
}

// SCHOLARSHIP PROGRAM
export interface ScholarshipProgram {
  id: number;
  name: string;
  description: string;
  sponsor: string;
  benefits: string;
  numberOfSlots: number;
  maxApplicants: number;
  eligibilityRequirements: string;
  openingDate: Date;
  closingDate: Date;
  academicYear: string;
  status: string;
  createdBy: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateScholarshipProgramRequest {
  name: string;
  description: string;
  sponsor: string;
  benefits: string;
  numberOfSlots: number;
  maxApplicants: number;
  eligibilityRequirements: string;
  openingDate: string;
  closingDate: string;
  academicYear: string;
  requiredDocuments?: string[];
}

export interface UpdateScholarshipProgramRequest {
  name?: string;
  description?: string;
  sponsor?: string;
  benefits?: string;
  eligibilityRequirements?: string;
  openingDate?: string;
  closingDate?: string;
  academicYear?: string;
  status?: string;
  numberOfSlots?: number;
  maxApplicants?: number;
  // When provided, fully replaces this program's required-document list
  // (matches the comma-separated overwrite semantics used at creation).
  requiredDocuments?: string[];
}

// REQUIRED DOCUMENT
export interface RequiredDocument {
  id: number;
  scholarshipId: number;
  documentType: string;
  description: string;
  isRequired: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Global (not per-program) list of document types every applicant must
// upload to their profile before submitting any application.
export interface DocumentRequirement {
  id: number;
  documentType: string;
  // False makes it an optional upload: listed, but never blocks a profile.
  isRequired: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// APPLICATION
export interface Application {
  id: number;
  applicantId: number;
  scholarshipId: number;
  status: ApplicationStatus;
  submissionDate?: Date;
  receivedDate?: Date;
  reviewedBy?: number;
  reviewedAt?: Date;
  comments?: string;
  createdAt: Date;
  updatedAt: Date;
  scholarshipName?: string;
  // The program's own status ('active'/'closed') — used to gate whether
  // the applicant can still edit their profile / request a renewal.
  scholarshipStatus?: string;
  applicantName?: string;
  applicantEmail?: string;
  applicantBarangay?: string;
  applicantAddress?: string;
  // Backs the Senior High School/ALS/College/Special Course filter on
  // the Application Review page.
  applicantYearLevel?: string;
}

// Flattened applicant profile + application row for the admin Reports
// page — one row per application, spreadsheet-style. Mirrors every field
// collected on the Profile page (client/src/pages/ProfilePage.tsx),
// excluding zipCode/previousSchool since those were deliberately removed
// from the profile UI (deprecated, not "profile information" anymore).
export interface ApplicationReportRow {
  applicationId: number;
  applicantId: number;
  lastName: string;
  firstName: string;
  middleName?: string;
  suffix?: string;
  sex?: string;
  civilStatus?: string;
  dateOfBirth?: Date;
  age?: number;
  placeOfBirth?: string;
  nationality?: string;
  idType?: string;
  idNumber?: string;
  isIndigenousPeople?: boolean;
  ipGroupTribe?: string;
  address?: string;
  barangay?: string;
  municipality?: string;
  province?: string;
  contactNumber?: string;
  email: string;
  sectoralClassifications: string[];
  sectoralClassificationOther?: string;
  numberOfHouseholdMembers?: number;
  numberOfDependentsStudying?: number;
  parentalStatus?: string;
  fatherName?: string;
  fatherOccupation?: string;
  fatherMonthlyIncome?: number;
  fatherEducationalAttainment?: string;
  motherName?: string;
  motherOccupation?: string;
  motherMonthlyIncome?: number;
  motherEducationalAttainment?: string;
  guardianName?: string;
  guardianOccupation?: string;
  guardianMonthlyIncome?: number;
  guardianEducationalAttainment?: string;
  householdMonthlyIncome?: number;
  schoolName?: string;
  schoolAddress?: string;
  schoolType?: string;
  courseName?: string;
  yearLevel?: string;
  gwa?: number;
  honorsAwards?: string;
  academicStatus?: string;
  currentlyReceivingAssistance?: boolean;
  currentAssistanceProgram?: string;
  currentAssistanceAmount?: number;
  appliedOtherScholarship?: boolean;
  otherScholarshipProgram?: string;
  academicDistinctionExtracurricular?: string;
  lbpAtmAccountNumber?: string;
  scholarshipName?: string;
  status: ApplicationStatus;
  scholarIdNumber?: string;
  submissionDate?: Date;
  createdAt: Date;
}

export interface ApplicationReportFilters {
  name?: string;
  barangay?: string;
  status?: ApplicationStatus;
  scholarshipId?: number;
  // 'senior_high' | 'college' | 'special_course' | 'als' — see
  // client/src/constants/yearLevelCategory.ts.
  yearLevelGroup?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateApplicationRequest {
  scholarshipId: number;
}

export interface UpdateApplicationRequest {
  status?: ApplicationStatus;
  comments?: string;
  receivedDate?: string;
}

export interface SubmitApplicationRequest {
  applicationId: number;
}

// APPLICATION STATUS HISTORY
export interface ApplicationStatusHistory {
  id: number;
  applicationId: number;
  previousStatus?: ApplicationStatus;
  newStatus: ApplicationStatus;
  changedBy: number;
  notes?: string;
  createdAt: Date;
}

// UPLOADED DOCUMENT
export interface UploadedDocument {
  id: number;
  applicationId?: number;
  userId: number;
  documentType?: string;
  fileName: string;
  filePath: string;
  fileSize?: number;
  fileType?: string;
  verificationStatus: DocumentVerificationStatus;
  verifiedBy?: number;
  verifiedAt?: Date;
  verificationNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface DocumentUploadRequest {
  applicationId?: number;
  documentType: string;
  file: File;
}

// SCHOLAR
export interface Scholar {
  id: number;
  userId: number;
  scholarshipId: number;
  scholarIdNumber?: string;
  approvalDate?: Date;
  qrCode?: string;
  status: ScholarStatus;
  createdAt: Date;
  updatedAt: Date;
  scholarshipName?: string;
  // The program's own status ('active'/'closed') — used to gate whether
  // the scholar can still request a renewal.
  scholarshipStatus?: string;
  studentName?: string;
  // Split out alongside studentName (which stays "First Last" for
  // everywhere else that already renders it that way) so Payroll can
  // format "Last, First M." without a second request.
  studentFirstName?: string;
  studentLastName?: string;
  studentMiddleName?: string;
  studentEmail?: string;
  studentBarangay?: string;
  studentAddress?: string;
  // The originating (approved) application's submissionDate/receivedDate —
  // lets the Scholar Management page order scholars within a program by
  // who submitted first, and display both dates.
  submissionDate?: Date;
  receivedDate?: Date;
  // The applicant's Year Level, from their profile — lets Payroll classify
  // scholars into Senior High/College/Special Course/ALS without a second
  // request.
  yearLevel?: string;
  // Whether this scholar has ever filed a renewal request — backs
  // Payroll's "New Scholar" vs "For Renewal" filter.
  hasRenewalRequest?: boolean;
}

// GRADE
export interface Grade {
  id: number;
  scholarId: number;
  academicYear?: string;
  semester?: string;
  gpa?: number;
  subjectDetails?: any;
  uploadedBy?: number;
  uploadedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface SubmitGradeRequest {
  scholarId: number;
  academicYear: string;
  semester: string;
  gpa: number;
  subjectDetails?: any;
}

// RENEWAL
export interface Renewal {
  id: number;
  scholarId: number;
  academicYear?: string;
  semester?: string;
  status: RenewalStatus;
  submissionDate?: Date;
  reviewedBy?: number;
  reviewedAt?: Date;
  decision?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  // Populated on the system-wide list (Renewal Requests page) and the
  // per-scholar list — undefined on a bare create/review response, which
  // doesn't need them since the caller already knows which scholar.
  studentName?: string;
  studentFirstName?: string;
  studentLastName?: string;
  studentMiddleName?: string;
  studentBarangay?: string;
  scholarshipId?: number;
  scholarshipName?: string;
  scholarIdNumber?: string;
}

export interface RenewalFilters {
  status?: RenewalStatus;
  scholarshipId?: number;
  page?: number;
  pageSize?: number;
}

export interface CreateRenewalRequest {
  scholarId: number;
  academicYear: string;
  semester: string;
}

export interface ReviewRenewalRequest {
  renewalId: number;
  decision: 'approved' | 'rejected';
  notes?: string;
}

// ALLOWANCE
export interface Allowance {
  id: number;
  scholarId: number;
  academicYear?: string;
  semester?: string;
  amount: number;
  paymentDate?: Date;
  status: AllowanceStatus;
  releaseDate?: Date;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateAllowanceRequest {
  scholarId: number;
  academicYear: string;
  semester: string;
  amount: number;
  paymentDate?: string;
}

// VIOLATION
export interface Violation {
  id: number;
  scholarId: number;
  violationType?: string;
  description?: string;
  severity?: ViolationSeverity;
  actionTaken?: string;
  resolved: boolean;
  resolvedDate?: Date;
  createdBy?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateViolationRequest {
  scholarId: number;
  violationType: string;
  description: string;
  severity: ViolationSeverity;
  actionTaken: string;
}

// ANNOUNCEMENT
export interface Announcement {
  id: number;
  title: string;
  content: string;
  announcementType?: AnnouncementType;
  pinned: boolean;
  pinnedUntil?: Date;
  createdBy: number;
  publishedAt?: Date;
  imageUrl?: string;
  attachments?: AnnouncementAttachment[];
  createdAt: Date;
  updatedAt: Date;
}

export interface AnnouncementAttachment {
  id: number;
  announcementId: number;
  fileName: string;
  filePath: string;
  fileType: string;
}

export interface CreateAnnouncementRequest {
  title: string;
  content: string;
  announcementType: AnnouncementType;
  imageUrl?: string;
  pinned?: boolean;
  pinnedUntil?: string;
}

// NOTIFICATION
export interface Notification {
  id: number;
  userId: number;
  title?: string;
  message?: string;
  notificationType?: NotificationType;
  isRead: boolean;
  readAt?: Date;
  actionUrl?: string;
  createdAt: Date;
}

// AUDIT LOG
export interface AuditLog {
  id: number;
  userId?: number;
  action: string;
  entityType?: string;
  entityId?: number;
  oldValues?: any;
  newValues?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: Date;
  // Present on GET /audit-logs and /audit-logs/deletions (both include
  // the actor); absent nowhere currently, but kept optional to match the
  // server's Prisma include shape defensively.
  user?: { email: string; firstName: string; lastName: string; role?: UserRole };
}

// DELETION APPROVAL WORKFLOW
export enum DeletionEntityType {
  APPLICATION = 'application',
  SCHOLAR = 'scholar',
  USER = 'user'
}

export enum DeletionRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface DeletionRequest {
  id: number;
  entityType: DeletionEntityType;
  entityId: number;
  entityLabel: string;
  // Undefined once the requester's own account has since been deleted.
  requestedBy?: number;
  requestedAt: Date;
  status: DeletionRequestStatus;
  reviewedBy?: number;
  reviewedAt?: Date;
  reviewNote?: string;
  requester?: { email: string; firstName: string; lastName: string };
  reviewerUser?: { email: string; firstName: string; lastName: string };
}

// Shape returned by DELETE /applications/:id, /scholars/:id, /users/:id —
// 'pending' means an Admin's delete was held for approval rather than
// carried out immediately (see server/src/services/*.ts).
export interface DeleteResult {
  status: 'deleted' | 'pending';
  message: string;
}

// DASHBOARD STATS
export interface DashboardStats {
  totalScholars: number;
  activeScholars: number;
  graduatedScholars: number;
  pendingApplications: number;
  approvedApplications: number;
  rejectedApplications: number;
  scholarshipUtilization: number;
  renewalsDue: number;
  // Scholars only (i.e. approved applications that became a Scholar
  // record), broken down by year-level category — not every applicant
  // profile matching that yearLevel regardless of application status.
  seniorHighScholars: number;
  collegeScholars: number;
  specialCourseScholars: number;
  alsScholars: number;
  // Pending (i.e. awaiting a decision) applications broken down by the
  // applicant's year-level category — same four groups as the scholar
  // counts above, so the two rows on the dashboard read together.
  pendingSeniorHighApplications: number;
  pendingCollegeApplications: number;
  pendingSpecialCourseApplications: number;
  pendingAlsApplications: number;
}

// API RESPONSE WRAPPERS
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// PAGINATION
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// FILTERS
export interface ApplicationFilters extends PaginationParams {
  status?: ApplicationStatus;
  scholarshipId?: number;
  applicantId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface ScholarFilters extends PaginationParams {
  status?: ScholarStatus;
  scholarshipId?: number;
  municipality?: string;
  academicYear?: string;
}

export interface AnnouncementFilters extends PaginationParams {
  type?: AnnouncementType;
  pinned?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

// ERROR
export interface ApiError {
  code: string;
  message: string;
  statusCode: number;
  details?: any;
}

// ============== PUBLIC HOME PAGE CONTENT ==============

// Every string the Super Admin can edit on the public landing page.
// Kept as one nested object (persisted as a single JSON row) rather than
// a table of key/value labels: the page is edited as a whole, never
// queried by key, and a new section is then just a new field here.
//
// The stored JSON need not be complete: a blob written before a field
// existed falls back to DEFAULT_HOME_CONTENT for it on read, so adding
// copy to the page never needs a data backfill.
export interface HomeContentLink {
  label: string;
  value: string;
}

export interface HomeContentCard {
  title: string;
  body: string;
}

export interface HomeContentSection {
  title: string;
  intro: string;
  cards: HomeContentCard[];
}

export interface HomeContent {
  brand: {
    acronym: string;
    fullName: string;
  };
  nav: {
    home: string;
    about: string;
    programs: string;
    guidelines: string;
    contact: string;
    signIn: string;
  };
  hero: {
    // Split into three so the middle word can be colour-accented
    // without the editor having to write markup.
    titleLead: string;
    titleAccent: string;
    titleRest: string;
    steps: string[];
    description: string;
    primaryCta: string;
    secondaryCta: string;
    highlights: HomeContentCard[];
  };
  about: HomeContentSection;
  programs: HomeContentSection;
  guidelines: HomeContentSection;
  contact: {
    title: string;
    intro: string;
    details: HomeContentLink[];
  };
  footer: {
    note: string;
  };
}

// What the public page renders before anything has ever been saved, and
// what every partially-populated stored blob is merged over.
export const DEFAULT_HOME_CONTENT: HomeContent = {
  brand: {
    acronym: 'ECEAP',
    fullName: 'Enhanced Conner Educational Assistance Program'
  },
  nav: {
    home: 'Home',
    about: 'About',
    programs: 'Programs',
    guidelines: 'Guidelines',
    contact: 'Contact',
    signIn: 'Sign In'
  },
  hero: {
    titleLead: 'Your',
    titleAccent: 'ECEAP',
    titleRest: 'Application, Made Simple.',
    steps: ['Apply.', 'Verify.', 'Track.', 'Renew.'],
    description:
      'Apply online, submit documents, track your application, and manage renewals — all in one secure platform.',
    primaryCta: 'Apply Now',
    secondaryCta: 'Sign In',
    highlights: [
      { title: 'Secure', body: 'Your data is safe with us.' },
      { title: 'Easy & Fast', body: 'Simple steps to complete.' },
      { title: 'Here to Help', body: "We're here to support you." }
    ]
  },
  about: {
    title: 'About ECEAP',
    intro:
      'The Enhanced Conner Educational Assistance Program is the Municipality of Conner, Apayao’s educational assistance program for its students.',
    cards: [
      {
        title: 'Who we are',
        body: 'A municipal program run by the Office of the Municipal Planning and Development Coordinator (MPDC), supporting Conner students through school.'
      },
      {
        title: 'What we do',
        body: 'We receive and evaluate applications, verify documents, award assistance, and manage renewals for continuing scholars.'
      },
      {
        title: 'Who we serve',
        body: 'Senior High School, ALS, College, and Special Course students who are bona fide residents of Conner, Apayao.'
      }
    ]
  },
  programs: {
    title: 'Programs',
    intro: 'Educational assistance is offered across the following levels.',
    cards: [
      {
        title: 'Senior High School',
        body: 'For Grade 11 and Grade 12 students enrolled in a public or private senior high school.'
      },
      {
        title: 'College',
        body: 'For students enrolled in a degree program, from first year through to fifth year.'
      },
      {
        title: 'Special Course',
        body: 'For students in professional and post-graduate courses such as Juris Doctor, Doctor of Veterinary Medicine, and Doctor of Medicine.'
      },
      {
        title: 'Alternative Learning System',
        body: 'For learners enrolled under the Alternative Learning System.'
      }
    ]
  },
  guidelines: {
    title: 'Guidelines',
    intro: 'Read these before you apply.',
    cards: [
      {
        title: 'Eligibility',
        body: 'You must be a bona fide resident of Conner, Apayao and currently enrolled. Type "N/A" on any profile field that does not apply to you.'
      },
      {
        title: 'Complete your profile',
        body: 'Fill in every section of your profile and upload the required documents. An incomplete profile cannot be submitted for review.'
      },
      {
        title: 'Certify and submit',
        body: 'Tick the certification and save your profile, then download and print your pre-filled application form and attach a 2" x 2" ID photo.'
      },
      {
        title: 'Submit the hard copy',
        body: 'Once approved for preliminary evaluation, submit the original, authenticated hard copies of your documents to the MPDC office.'
      }
    ]
  },
  contact: {
    title: 'Contact',
    intro: 'Reach the ECEAP focal persons at the Municipal Hall.',
    details: [
      { label: 'Office', value: 'Office of the Municipal Planning and Development Coordinator (MPDC)' },
      { label: 'Address', value: 'Municipal Hall, Conner, Apayao' },
      { label: 'Email', value: 'eceap@conner.gov.ph' },
      { label: 'Office hours', value: 'Monday to Friday, 8:00 AM – 5:00 PM' }
    ]
  },
  footer: {
    note: 'Municipality of Conner, Province of Apayao'
  }
};

// Another applicant who may be the same person as the one being
// reviewed. Advisory only — see ApplicantService.findPossibleDuplicates
// for why this is surfaced to a reviewer rather than enforced as a rule.
export interface DuplicateApplicantMatch {
  applicantId: number;
  userId: number;
  fullName: string;
  email: string;
  idNumber?: string;
  contactNumber?: string;
  registeredAt: Date | string;
  /** Which fields overlapped, e.g. "Same phone number". */
  reasons: string[];
}

/**
 * One account that shares an identifying field with at least one other.
 * Backs the "Duplicate/Double Users" filter on User Management. Mirrors
 * server/src/types.ts.
 */
export interface DuplicateUserFlag {
  userId: number;
  /** Which fields overlapped, worded as the reviewer's list words them. */
  reasons: string[];
}

// ============== EDITABLE LEGAL DOCUMENTS ==============
// Mirrors server/src/lib/legalDocuments.ts. Only the shape is mirrored,
// not the default text — that is several hundred lines of prose written
// to be edited, so the server stays its single source and the editor
// fetches it (see apiService.getLegalDocumentDefault).

export interface LegalSectionContent {
  heading: string;
  /**
   * Plain text. A blank line starts a new paragraph, a line beginning
   * "- " is a bullet, and that is the whole grammar — it is rendered by
   * renderLegalBody in LegalDocumentView, never as HTML.
   */
  body: string;
}

export interface LegalDocumentContent {
  title: string;
  lastUpdated: string;
  /** Renders a "not yet in force" banner while set. */
  draft: boolean;
  sections: LegalSectionContent[];
  /**
   * Content hash, computed by the server when the document is served.
   * Sent back when an applicant agrees to it, so the consent record
   * names the exact wording they were shown. Read-only: the server
   * strips it from anything the editor submits.
   */
  version?: string;
}

export type LegalDocumentKey = 'privacy' | 'terms';

// ============== ABOUT US ==============
// Super-Admin-editable, publicly viewable. Same {heading, body} section
// shape as the legal documents, so it renders through LegalDocumentView.
export interface AboutContent {
  title: string;
  sections: LegalSectionContent[];
}

export const UNAVAILABLE_ABOUT: AboutContent = {
  title: 'About Us',
  sections: [
    {
      heading: 'This page could not be loaded',
      body: 'Please check your connection and reload the page.'
    }
  ]
};

/**
 * Shown only when the document cannot be fetched at all. Deliberately
 * not a copy of the real text: a stale privacy notice baked into the
 * bundle would be worse than an honest "this didn't load", because a
 * reader cannot tell the difference.
 */
export const UNAVAILABLE_LEGAL_DOCUMENT: LegalDocumentContent = {
  title: 'Document unavailable',
  lastUpdated: '',
  draft: false,
  sections: [
    {
      heading: 'This document could not be loaded',
      body: 'Please check your connection and reload the page. If it keeps happening, contact the programme before submitting any personal information.'
    }
  ]
};

// PAYROLL

export type PayrollActivityAction =
  | 'scholar_removed'
  | 'scholar_added'
  | 'signatory_changed'
  | 'amount_changed'
  | 'semester_changed'
  | 'printed'
  | 'excel_downloaded';

export interface PayrollActivityEntry {
  id: number;
  action: PayrollActivityAction;
  details?: Record<string, any> | null;
  createdAt: string;
  userName: string;
  userRole?: UserRole;
}

export interface PayrollSignatory {
  name: string;
  title: string;
}

export interface SavePayrollRequest {
  // 'excel' carries the downloaded .xlsx itself, base64-encoded.
  format: 'pdf' | 'excel';
  fileBase64?: string;
  scholarIds: number[];
  amount: number;
  periodLabel: string;
  month?: string;
  semester?: string;
  signatories: {
    preparedBy: PayrollSignatory;
    certifying1: PayrollSignatory;
    certifying2: PayrollSignatory;
    approvedBy: PayrollSignatory;
  };
}

export interface PayrollRecord {
  id: number;
  format: 'pdf' | 'excel';
  createdAt: string;
  periodLabel: string;
  month?: string;
  semester?: string;
  amount: number;
  scholarCount: number;
  total: number;
  fileName: string;
  printedBy: string;
}
